import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, X, Maximize2 } from 'lucide-react';

const diagrams = [
  {
    src: '/tlm/digital-tlm-framework.png',
    label: 'The Digital TLM Engine Framework — seven connected engines',
    alt: "Mashmari Digital TLM Engine Framework diagram: seven connected engines (Curriculum & Knowledge Intelligence, Competency & Learning Intelligence, Lesson Intelligence, Digital TLM Factory, Teacher Capability Engine, Classroom Delivery Engine and Evidence & Improvement Engine) forming one continuous learning cycle around Classroom Execution",
  },
  {
    src: '/tlm/digital-tlm-implementation.png',
    label: 'Implementation Approach — seven stages over 20 weeks',
    alt: 'Mashmari Digital TLM Engine Implementation diagram: a seven-stage, 20-week rollout for Grades 3–5 English & Mathematics — Engage the Experts, Define Success, Review and Upgrade, Prepare Teachers and Pipeline, Co-create and Execute, Review and Refine, and Verify and Decide',
  },
];

export default function TlmDiagramSlideshow() {
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const count = diagrams.length;
  const go = useCallback((next) => setIndex((i) => (next + count) % count), [count]);
  const current = diagrams[index];

  useEffect(() => { setZoomed(false); }, [index]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(false);
      if (e.key === 'ArrowRight') go(index + 1);
      if (e.key === 'ArrowLeft') go(index - 1);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [lightbox, index, go]);

  return (
    <div data-testid="tlm-slideshow">
      {/* Full, uncropped diagram on a clean light surface — no overlays */}
      <div className="relative rounded-3xl border border-[var(--mm-line)] bg-white overflow-hidden shadow-[0_28px_70px_-40px_rgba(74,18,89,.4)]">
        <button
          type="button"
          onClick={() => { setZoomed(false); setLightbox(true); }}
          aria-label={`Enlarge diagram: ${current.label}`}
          data-testid="tlm-open-lightbox"
          className="block w-full cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mm-purple)]"
        >
          <img
            key={current.src}
            src={current.src}
            alt={current.alt}
            data-testid={`tlm-image-${index}`}
            className="w-full aspect-video object-contain bg-white"
          />
        </button>
      </div>

      {/* Controls below the image so nothing covers the diagram */}
      <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
        <div className="flex items-center gap-3">
          <button onClick={() => go(index - 1)} aria-label="Previous diagram" data-testid="tlm-slide-prev"
            className="w-11 h-11 rounded-full border border-[var(--mm-line)] bg-white text-[var(--mm-purple)] flex items-center justify-center hover:border-[var(--mm-purple)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mm-purple)]">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2" role="tablist" aria-label="Choose diagram">
            {diagrams.map((d, i) => (
              <button key={d.src} onClick={() => setIndex(i)} role="tab" aria-selected={i === index}
                aria-label={`Diagram ${i + 1}: ${d.label}`} data-testid={`tlm-dot-${i}`}
                className={`h-2.5 rounded-full transition-all ${i === index ? 'w-8 bg-[var(--mm-purple)]' : 'w-2.5 bg-[var(--mm-purple)]/25 hover:bg-[var(--mm-purple)]/45'}`} />
            ))}
          </div>
          <button onClick={() => go(index + 1)} aria-label="Next diagram" data-testid="tlm-slide-next"
            className="w-11 h-11 rounded-full border border-[var(--mm-line)] bg-white text-[var(--mm-purple)] flex items-center justify-center hover:border-[var(--mm-purple)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mm-purple)]">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 flex items-center justify-between gap-4">
          <p className="text-sm font-semibold text-[var(--mm-ink)]" data-testid="tlm-slide-label">
            <span className="text-[var(--mm-purple)]">{index + 1}/{count}</span> &nbsp;{current.label}
          </p>
          <button onClick={() => { setZoomed(false); setLightbox(true); }} data-testid="tlm-enlarge"
            className="shrink-0 inline-flex items-center gap-2 text-xs font-semibold text-[var(--mm-purple)] border border-[var(--mm-line)] hover:border-[var(--mm-purple)] rounded-full px-4 py-2.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mm-purple)]">
            Enlarge to read <Maximize2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <p className="mt-3 text-xs text-[var(--mm-ink)]/55 sm:hidden">Tap the diagram to open a larger, zoomable view.</p>

      {lightbox && createPortal((
        <div className="fixed inset-0 z-[9999] flex flex-col" role="dialog" aria-modal="true"
          style={{ backgroundColor: '#1c0722' }}
          aria-label={current.label} data-testid="tlm-lightbox">
          <div className="flex items-center justify-between gap-4 px-4 md:px-6 py-3 text-white">
            <p className="text-sm font-semibold truncate">{current.label}</p>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => setZoomed((z) => !z)} data-testid="tlm-lightbox-zoom"
                aria-label={zoomed ? 'Fit diagram to screen' : 'Zoom in to actual size'}
                className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
                {zoomed ? <ZoomOut className="w-5 h-5" /> : <ZoomIn className="w-5 h-5" />}
              </button>
              <button onClick={() => setLightbox(false)} data-testid="tlm-lightbox-close" aria-label="Close"
                className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-auto flex items-center justify-center p-3 md:p-6" style={{ touchAction: 'pinch-zoom' }}
            data-testid="tlm-lightbox-scroll">
            <img
              src={current.src}
              alt={current.alt}
              onClick={() => setZoomed((z) => !z)}
              className={zoomed
                ? 'max-w-none w-[1672px] max-h-none cursor-zoom-out'
                : 'max-w-full max-h-full object-contain cursor-zoom-in'}
            />
          </div>

          <div className="flex items-center justify-center gap-4 px-4 py-4">
            <button onClick={() => go(index - 1)} aria-label="Previous diagram" data-testid="tlm-lightbox-prev"
              className="w-11 h-11 rounded-full bg-white/90 hover:bg-white text-[var(--mm-purple)] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              {diagrams.map((d, i) => (
                <button key={d.src} onClick={() => setIndex(i)} aria-label={`Diagram ${i + 1}`} data-testid={`tlm-lightbox-dot-${i}`}
                  className={`h-2.5 rounded-full transition-all ${i === index ? 'w-8 bg-white' : 'w-2.5 bg-white/40 hover:bg-white/70'}`} />
              ))}
            </div>
            <button onClick={() => go(index + 1)} aria-label="Next diagram" data-testid="tlm-lightbox-next"
              className="w-11 h-11 rounded-full bg-white/90 hover:bg-white text-[var(--mm-purple)] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
